import { createContext, useContext } from 'react';
import { DEFAULT_LANG, type Lang } from '@/features/survey/i18n';
import { makeFormat, type Fmt } from './format';

/*
 * French and English for the Minions dashboard, from the design's MN_I18N.
 * French is the default; the language toggle and the remembered choice are the
 * Système U ones (features/survey/i18n.ts), so a viewer who reads the quiz in
 * English opens Minions in English too.
 *
 * `MinionsStrings` is the English dictionary's type, so a key missing from the
 * French one — or a function taking other arguments — fails the type check.
 * French follows its own typography: no-break spaces before ":" and "%",
 * typographic apostrophes.
 */

const NBSP = '\u00a0';

const en = {
  // Header
  title: 'Système U — Minions — Pinball',
  subtitle: 'Campaign dashboard',
  noLiveData: 'No live data',
  live: 'Live',
  notStarted: 'Not started',
  ended: 'Ended',
  updatedAt: (time: string) => `· updated at ${time}`,
  period: 'Period',
  exportLabel: 'Export',
  report: 'Report',
  reportPlaceholder: 'Report…',
  reportsActive: 'Active',
  reportsArchived: 'Archived',
  users: 'Users',
  signOut: 'Sign out',
  language: 'Language',

  // Weeks
  week: (n: number) => `Week ${n}`,
  weekShort: (n: number) => `Wk${n}`,
  inProgress: 'in progress',
  weekStatus: { done: 'Done', live: 'In progress', planned: 'Planned' },

  // Take-away
  tk1: 'unique players so far ·',
  tk3: 'games per player.',

  // Reach
  uniquePlayers: 'Unique players',
  weekTag: (n: number, total: number) => `Week ${n} / ${total}`,
  funnelSub: 'conversion funnel from first visit to the first game',
  funnelFoot: 'Unique players = people who accepted the opt-in and played at least one game. The step with the biggest drop is shown in red.',
  funnel: {
    uniqueVisitors: 'Unique visitors',
    optIns: 'Opt-ins (rules accepted)',
    uniquePlayers: 'Unique players (played a game)',
  },
  lost: (n: string) => `−${n} lost at this step`,

  // Headline tiles
  kPlays: 'Game plays',
  kPlaysSub: 'pinball games played, whole campaign',
  kPpu: 'Games per player',
  kPpuSub: (daily: number) => `${daily} free game${daily === 1 ? '' : 's'} a day + extra games from missions`,
  kTickets: 'Lottery tickets',
  kTicketsSub: 'every game earns one ticket for the grand draw',
  kTime: 'Total time in the app',
  kTimeSub: 'all users, all sessions',
  kSession: 'Avg. session duration',
  kSessionSub: 'per visit ·',
  kSessionSub2: 'per player, whole campaign',

  // Daily
  dailyTitle: 'Daily unique visitors and active players',
  lgVisitors: 'Unique visitors',
  lgDau: 'Active players (DAU)',
  dailyNote:
    "A visitor who comes back on another day counts again on that day, so the days add up to more than the campaign's unique visitors. The current day is shown in red.",
  dailyEmpty: 'The daily figures appear after the first pipeline run.',
  dailyShow: 'Show the figures per day',
  dailyChartLabel: 'Unique visitors and active players per day — use the arrow keys to read each day',
  cDay: 'Day',
  cVisitors: 'Unique visitors',
  cDau: 'Active players',
  cPlays: 'Games played',

  // Engagement
  sEngagement: 'Engagement and retention',
  pendingFrom: (day: string) => `From ${day}`,
  pendingSub: 'once week 1 is complete',
  dwTitle: 'DAU / WAU',
  dwDau: 'Avg. daily active players',
  dwDau7: 'DAU, last 7 days',
  dwWau: 'WAU, last full week',
  dwStickiness: 'Stickiness (DAU ÷ WAU)',
  dwNote: "Distinct players with at least one game that day or week. Stickiness shows how many of the week's players come back on a typical day.",
  rtTitle: 'Retention',
  rtD1: 'Day-1',
  rtD7: 'Day-7',
  rtRecurring: 'Recurring',
  rtNote1: 'Share of new players who come back 1 and 7 days after their first game.',
  rtNote2: 'recurring players played on more than one day.',
  rtPending: 'The figures appear once week 1 is complete.',
  wpTitle: 'Unique players per week',
  wpNote: 'Players with at least one game that week. Weeks run Tuesday to Monday; the current week is in red.',
  dsTitle: 'Games per player, whole campaign',
  dsNote: 'Share of unique players by the number of games they played. One free game a day; missions add extra games.',
  game: 'game',
  games: 'games',

  // Missions
  sMissions: 'Missions',
  lmTitle: 'Login mission',
  lmSub: 'of players completed it',
  lmCompleted: 'Players who completed',
  lmExtra: 'Extra games granted',
  lmNote: 'Log in on 5 days during the campaign, not necessarily in a row. Reward: 1 extra game, once.',
  rfTitle: 'Invite friends',
  rfRate: 'Invite rate',
  rfRateSub: 'players sent an invite',
  rfConv: 'Invite conversion',
  rfConvSub: 'invited friends played',
  rfSent: 'Invites sent',
  rfExtra: 'Extra games via invites',
  rfLimit: (cap: number) => `Reached the ${cap}-invite limit`,
  rfLimitSub: 'of all players',
  rfNote: (cap: number) =>
    `Invite rate = players who sent at least one invite. Conversion = invited friends who accepted the opt-in and played. A code earns its owner 1 extra game for each of the first ${cap} friends who join with it; the ${cap}-invite limit share is counted against all unique players.`,

  // Consents and time
  mcTitle: 'Marketing consents',
  mcSub: 'players gave a marketing consent',
  mcEmail: 'Email',
  mcSms: 'SMS',
  mcEmailConsents: 'Email consents',
  mcSmsConsents: 'SMS consents',
  mcOfOptIns: 'of opt-ins',
  mcDonutLabel: 'Marketing consents by channel',
  tmTitle: 'Time spent in the app per week',
  tmNote: 'Total hours across all sessions. The weekly summary below shows the time per player.',

  // Weekly summary
  smTitle: 'Weekly summary',
  cWeek: 'Week',
  cUniquePlayers: 'Unique players',
  cNewPlayers: 'New players',
  cPpu: 'Games / player',
  cTickets: 'Tickets',
  cLoginMissions: 'Login missions',
  cInvites: 'Invites sent',
  cTime: 'Time in app',
  cTpu: 'Time / player',
  smNote: 'Weekly unique players are counted per week, so their sum is higher than the campaign total.',

  foot: (period: string) =>
    `KPIs follow §15 “Success metrics / KPIs” of the Minions Development Team Spec. Targets: TBD with client / PM. Campaign: ${period}, Paris time; weeks run Tuesday to Monday.`,

  // CSV export (opened in Excel: plain headers, the decimal mark of the language)
  csvDecimal: '.',
  csvTotal: 'Total',
  csvWeekly: {
    week: 'Week',
    start: 'Start',
    end: 'End',
    status: 'Status',
    uniquePlayers: 'Unique players',
    newPlayers: 'New players',
    plays: 'Games played',
    playsPerPlayer: 'Games per player',
    tickets: 'Lottery tickets',
    loginMissions: 'Login missions',
    invitesSent: 'Invites sent',
    timeHours: 'Time in app (h)',
    timePerPlayerSec: 'Time per player (s)',
  },
};

export type MinionsStrings = typeof en;

const fr: MinionsStrings = {
  // En-tête
  title: 'Système U — Minions — Flipper',
  subtitle: 'Tableau de bord de la campagne',
  noLiveData: 'Pas de données en direct',
  live: 'En direct',
  notStarted: 'Pas commencée',
  ended: 'Terminée',
  updatedAt: (time) => `· mis à jour à ${time}`,
  period: 'Période',
  exportLabel: 'Exporter',
  report: 'Rapport',
  reportPlaceholder: 'Rapport…',
  reportsActive: 'En cours',
  reportsArchived: 'Archivés',
  users: 'Utilisateurs',
  signOut: 'Déconnexion',
  language: 'Langue',

  // Semaines
  week: (n) => `Semaine ${n}`,
  weekShort: (n) => `S${n}`,
  inProgress: 'en cours',
  weekStatus: { done: 'Terminée', live: 'En cours', planned: 'À venir' },

  // Synthèse
  tk1: 'joueurs uniques à ce jour ·',
  tk3: 'parties par joueur.',

  // Audience
  uniquePlayers: 'Joueurs uniques',
  weekTag: (n, total) => `Semaine ${n} / ${total}`,
  funnelSub: 'tunnel de conversion, de la première visite à la première partie',
  funnelFoot: 'Joueurs uniques = personnes ayant accepté l’opt-in et joué au moins une partie. L’étape avec la plus forte perte est en rouge.',
  funnel: {
    uniqueVisitors: 'Visiteurs uniques',
    optIns: 'Opt-ins (règlement accepté)',
    uniquePlayers: 'Joueurs uniques (au moins une partie)',
  },
  lost: (n) => `−${n} perdus à cette étape`,

  // Indicateurs
  kPlays: 'Parties jouées',
  kPlaysSub: 'parties de flipper, toute la campagne',
  kPpu: 'Parties par joueur',
  kPpuSub: (daily) => `${daily} partie${daily === 1 ? '' : 's'} gratuite${daily === 1 ? '' : 's'} par jour + parties bonus des missions`,
  kTickets: 'Tickets de tirage',
  kTicketsSub: 'chaque partie donne un ticket pour le grand tirage',
  kTime: 'Temps total dans l’app',
  kTimeSub: 'tous les utilisateurs, toutes les sessions',
  kSession: 'Durée moyenne de session',
  kSessionSub: 'par visite ·',
  kSessionSub2: 'par joueur, toute la campagne',

  // Par jour
  dailyTitle: 'Visiteurs uniques et joueurs actifs par jour',
  lgVisitors: 'Visiteurs uniques',
  lgDau: 'Joueurs actifs (DAU)',
  dailyNote:
    'Un visiteur qui revient un autre jour est compté à nouveau ce jour-là : la somme des jours dépasse donc le nombre de visiteurs uniques de la campagne. Le jour en cours est en rouge.',
  dailyEmpty: 'Les chiffres par jour apparaissent après la première mise à jour des données.',
  dailyShow: 'Afficher les chiffres par jour',
  dailyChartLabel: 'Visiteurs uniques et joueurs actifs par jour — utilisez les flèches pour lire chaque jour',
  cDay: 'Jour',
  cVisitors: 'Visiteurs uniques',
  cDau: 'Joueurs actifs',
  cPlays: 'Parties jouées',

  // Engagement
  sEngagement: 'Engagement et fidélisation',
  pendingFrom: (day) => `Dès le ${day}`,
  pendingSub: 'à la fin de la semaine 1',
  dwTitle: 'DAU / WAU',
  dwDau: 'Joueurs actifs par jour (moy.)',
  dwDau7: 'DAU, 7 derniers jours',
  dwWau: 'WAU, dernière semaine complète',
  dwStickiness: 'Fidélité (DAU ÷ WAU)',
  dwNote:
    'Joueurs distincts ayant joué au moins une partie ce jour-là ou cette semaine. La fidélité indique la part des joueurs de la semaine qui reviennent un jour donné.',
  rtTitle: 'Rétention',
  rtD1: 'Jour 1',
  rtD7: 'Jour 7',
  rtRecurring: 'Récurrents',
  rtNote1: 'Part des nouveaux joueurs qui reviennent 1 et 7 jours après leur première partie.',
  rtNote2: 'joueurs récurrents ont joué sur plus d’un jour.',
  rtPending: 'Les chiffres apparaissent à la fin de la semaine 1.',
  wpTitle: 'Joueurs uniques par semaine',
  wpNote: 'Joueurs ayant joué au moins une partie dans la semaine. Les semaines vont du mardi au lundi ; la semaine en cours est en rouge.',
  dsTitle: 'Parties par joueur, toute la campagne',
  dsNote: 'Part des joueurs uniques selon le nombre de parties jouées. Une partie gratuite par jour ; les missions donnent des parties bonus.',
  game: 'partie',
  games: 'parties',

  // Missions
  sMissions: 'Missions',
  lmTitle: 'Mission connexion',
  lmSub: 'des joueurs l’ont terminée',
  lmCompleted: 'Joueurs l’ayant terminée',
  lmExtra: 'Parties bonus accordées',
  lmNote: 'Se connecter 5 jours pendant la campagne, pas forcément d’affilée. Récompense : 1 partie bonus, une seule fois.',
  rfTitle: 'Parrainage',
  rfRate: 'Taux de parrainage',
  rfRateSub: 'joueurs ont envoyé une invitation',
  rfConv: 'Conversion des invitations',
  rfConvSub: 'filleuls ont joué',
  rfSent: 'Invitations envoyées',
  rfExtra: 'Parties bonus via parrainage',
  rfLimit: (cap) => `Ont atteint la limite de ${cap} invitations`,
  rfLimitSub: 'de tous les joueurs',
  rfNote: (cap) =>
    `Taux de parrainage = joueurs ayant envoyé au moins une invitation. Conversion = filleuls ayant accepté l’opt-in et joué. Un code donne à son propriétaire 1 partie bonus pour chacun des ${cap} premiers amis qui l’utilisent ; la part ayant atteint la limite de ${cap} invitations est calculée sur tous les joueurs uniques.`,

  // Consentements et temps
  mcTitle: 'Consentements marketing',
  mcSub: 'joueurs ont donné un consentement marketing',
  mcEmail: 'E-mail',
  mcSms: 'SMS',
  mcEmailConsents: 'Consentements e-mail',
  mcSmsConsents: 'Consentements SMS',
  mcOfOptIns: 'des opt-ins',
  mcDonutLabel: 'Consentements marketing par canal',
  tmTitle: 'Temps passé dans l’app par semaine',
  tmNote: 'Total des heures sur toutes les sessions. Le récapitulatif ci-dessous donne le temps par joueur.',

  // Récapitulatif
  smTitle: 'Récapitulatif hebdomadaire',
  cWeek: 'Semaine',
  cUniquePlayers: 'Joueurs uniques',
  cNewPlayers: 'Nouveaux joueurs',
  cPpu: 'Parties / joueur',
  cTickets: 'Tickets',
  cLoginMissions: 'Missions connexion',
  cInvites: 'Invitations',
  cTime: 'Temps dans l’app',
  cTpu: 'Temps / joueur',
  smNote: 'Les joueurs uniques sont comptés par semaine : leur somme dépasse donc le total de la campagne.',

  foot: (period) =>
    `Les KPI suivent le §15 « Success metrics / KPIs » de la Development Team Spec Minions. Objectifs${NBSP}: à définir avec le client / PM. Campagne${NBSP}: ${period}, heure de Paris${NBSP}; les semaines vont du mardi au lundi.`,

  csvDecimal: ',',
  csvTotal: 'Total',
  csvWeekly: {
    week: 'Semaine',
    start: 'Début',
    end: 'Fin',
    status: 'Statut',
    uniquePlayers: 'Joueurs uniques',
    newPlayers: 'Nouveaux joueurs',
    plays: 'Parties jouées',
    playsPerPlayer: 'Parties par joueur',
    tickets: 'Tickets de tirage',
    loginMissions: 'Missions connexion',
    invitesSent: 'Invitations envoyées',
    timeHours: 'Temps dans l’app (h)',
    timePerPlayerSec: 'Temps par joueur (s)',
  },
};

export const MINIONS_STRINGS: Record<Lang, MinionsStrings> = { en, fr };

export type MinionsI18n = { lang: Lang; t: MinionsStrings; f: Fmt; setLang: (lang: Lang) => void };

/** French without a provider, so a component rendered on its own still reads. */
export const MinionsI18nContext = createContext<MinionsI18n>({
  lang: DEFAULT_LANG,
  t: MINIONS_STRINGS[DEFAULT_LANG],
  f: makeFormat(DEFAULT_LANG),
  setLang: () => {},
});

export const useMinionsI18n = (): MinionsI18n => useContext(MinionsI18nContext);
